import type { InvoiceSettings } from '@/lib/api/invoice-api'
import type { SalesOrder } from '@/lib/api/sales-order-api'
import { WALKIN_CUSTOMER } from '@/lib/constants'
import { escapeHtml } from '@/lib/html-escape'
import { formatVND } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export interface ReceiptItem {
    name: string
    unitPrice: number
    quantity: number
    /** Line subtotal as stored by the server (post line-discount). */
    lineTotal: number
}

export interface ReceiptTotals {
    subtotal: number
    discount: number
    vat: number
    total: number
}

export interface ReceiptData {
    companyName: string
    taxCode?: string
    address?: string
    orderCode: string
    createdAt: string
    cashierName?: string
    customerName?: string
    /** Địa chỉ giao đã ghép (đường, phường/xã, tỉnh/thành), rỗng thì ẩn dòng. */
    customerAddress?: string
    customerPhone?: string
    paymentMethod?: string
    vatPercent: number
    items: ReceiptItem[]
    totals: ReceiptTotals
    footerNote?: string
    /** Optional QR image (data URL) rendered above the footer. */
    qrDataUrl?: string
    /** Logo data URL fetched by the caller before printing to avoid race with the 100ms print timer. */
    logoDataUrl?: string
    /** Bank-transfer content line. Defaults to the order code for bank_transfer orders. */
    transferContent?: string
}

/**
 * Reproduces the payment-invoice arithmetic: subtotal from server line totals,
 * discount as gross (unit*qty) minus subtotal, VAT as the remainder baked into
 * total_amount. Kept in lockstep with the server PDF so both documents reconcile.
 */
export function computeReceiptTotals(
    items: ReceiptItem[],
    totalAmount: number,
    vatPercent: number,
): ReceiptTotals {
    let subtotalRaw = 0
    let grossRaw = 0
    for (const it of items) {
        subtotalRaw += it.lineTotal
        grossRaw += it.unitPrice * it.quantity
    }
    const subtotal = Math.round(subtotalRaw)
    const total = Math.round(totalAmount)
    const discount = Math.round(grossRaw) - subtotal
    const vat = vatPercent > 0 ? total - subtotal : 0
    return { subtotal, discount, vat, total }
}

/** Maps an internal payment method code to its localized label. */
export function paymentMethodLabel(method?: string): string {
    switch ((method ?? '').toLowerCase()) {
        case 'cash':
            return m.receipt_payment_cash()
        case 'bank_transfer':
            return m.receipt_payment_bank()
        case 'card':
            return m.receipt_payment_card()
        default:
            return method ?? ''
    }
}

/**
 * Ghép địa chỉ giao của đơn giống phiếu đóng gói phía server
 * (`orderFullAddress`): đường, phường/xã, tỉnh/thành — bỏ phần trống.
 */
export function orderFullAddress(
    order: Pick<
        SalesOrder,
        'shipping_address' | 'shipping_ward' | 'shipping_province'
    >,
): string {
    return [
        order.shipping_address,
        order.shipping_ward,
        order.shipping_province,
    ]
        .filter(Boolean)
        .join(', ')
}

/**
 * Địa chỉ in trên hóa đơn thanh toán: như `orderFullAddress` nhưng bỏ phần
 * đường khi đó là chuỗi giữ chỗ POS ghi vào đơn lúc khách không có địa chỉ
 * (khách lẻ "Khách vãng lai", khách trống địa chỉ "Mua tại quầy"). So sánh bỏ
 * khoảng trắng hai đầu, không phân biệt hoa thường — khớp server
 * `paymentInvoiceAddress`.
 */
export function paymentInvoiceAddress(
    order: Pick<
        SalesOrder,
        'shipping_address' | 'shipping_ward' | 'shipping_province'
    >,
): string {
    const street = (order.shipping_address ?? '').trim().toLowerCase()
    const placeholders = [WALKIN_CUSTOMER.address, m.customer_in_store()].map(
        (p) => (p ?? '').trim().toLowerCase(),
    )
    return orderFullAddress(
        placeholders.includes(street)
            ? { ...order, shipping_address: '' }
            : order,
    )
}

/** Vật tư có giá (giá vật tư) để tiêm thành dòng hàng trên receipt. */
export interface ReceiptMaterialLine {
    material_name: string
    unit_price: number
    quantity: number
    discount_percent?: number
}

/** Assembles the receipt view-model from an order detail + invoice settings.
 * `materials` (tuỳ chọn) là draft vật tư của đơn — dòng unit_price>0 được tiêm
 * vào bảng như một dòng hàng "(vật tư)". */
export function receiptDataFromOrder(
    order: SalesOrder,
    settings: InvoiceSettings,
    fallbackPaymentMethod?: string,
    materials?: ReceiptMaterialLine[],
): ReceiptData {
    const items: ReceiptItem[] = (order.items ?? []).map((it) => ({
        name: it.product_name,
        unitPrice: it.unit_price,
        quantity: it.quantity,
        lineTotal: it.subtotal,
    }))
    for (const mat of materials ?? []) {
        if (mat.unit_price > 0 && mat.quantity > 0) {
            items.push({
                name: `${mat.material_name} ${m.material_row_suffix()}`,
                unitPrice: mat.unit_price,
                quantity: mat.quantity,
                lineTotal:
                    mat.unit_price *
                    mat.quantity *
                    (1 - (mat.discount_percent ?? 0) / 100),
            })
        }
    }
    const effectivePaymentMethod = order.payment_method || fallbackPaymentMethod
    return {
        companyName: settings.company_name,
        taxCode: settings.company_tax_code,
        address: settings.company_address,
        orderCode: order.order_code,
        createdAt: order.created_at,
        cashierName: order.created_by_name,
        customerName: order.customer_name,
        customerAddress: paymentInvoiceAddress(order),
        customerPhone: order.customer_phone,
        paymentMethod: effectivePaymentMethod,
        vatPercent: settings.vat_percent,
        items,
        totals: computeReceiptTotals(
            items,
            order.total_amount,
            settings.vat_percent,
        ),
        footerNote: settings.footer_note,
        transferContent:
            effectivePaymentMethod === 'bank_transfer'
                ? order.order_code
                : undefined,
    }
}

/** Builds a self-contained HTML document sized for an 80mm thermal roll. */
export function buildReceiptHtml(data: ReceiptData): string {
    const d = new Date(data.createdAt)
    const dateLine = isNaN(d.getTime())
        ? ''
        : m.receipt_date({
              day: d.getDate(),
              month: String(d.getMonth() + 1).padStart(2, '0'),
              year: d.getFullYear(),
          })

    const rows = data.items
        .map(
            (it, i) => `<tr>
<td class="c">${i + 1}</td>
<td class="l">${escapeHtml(it.name)}</td>
<td class="r">${formatVND(it.unitPrice)}</td>
<td class="c">${it.quantity}</td>
<td class="r">${formatVND(it.lineTotal)}</td>
</tr>`,
        )
        .join('')

    const metaLine = (label: string, value: string) =>
        value ? `<div class="meta">${label}: ${escapeHtml(value)}</div>` : ''

    const totalRow = (label: string, value: number, strong = false) =>
        `<div class="trow${strong ? ' strong' : ''}"><span>${label}</span><span>${formatVND(value)}</span></div>`

    const vatRow =
        data.vatPercent > 0
            ? totalRow(
                  m.receipt_vat({ percent: data.vatPercent }),
                  data.totals.vat,
              )
            : ''

    const logoBlock = data.logoDataUrl
        ? `<div class="logo"><img src="${data.logoDataUrl}" alt="" /></div>`
        : ''

    const ckLine = data.transferContent
        ? `<div class="ck-content">${m.payment_qr_content()}: ${escapeHtml(data.transferContent)}</div>`
        : ''

    const qrBlock = data.qrDataUrl
        ? `<div class="qr"><img src="${data.qrDataUrl}" alt="QR" /></div>`
        : ''

    const footer = data.footerNote ?? m.receipt_thank_you()

    return `<!DOCTYPE html>
<html lang="vi"><head><meta charset="utf-8" />
<title>${escapeHtml(data.orderCode)}</title>
<style>
@page { size: 80mm auto; margin: 0; }
* { margin: 0; padding: 0; box-sizing: border-box; }
html, body { width: 80mm; }
body { font-family: 'Roboto', 'Segoe UI', system-ui, sans-serif; font-size: 13px; color: #000; padding: 2mm 3mm; line-height: 1.35; }
.center { text-align: center; }
.logo { text-align: center; margin-bottom: 3px; }
.logo img { width: 45mm; max-width: 45mm; height: auto; display: block; margin: 0 auto; }
.company { font-size: 15px; font-weight: 700; text-transform: uppercase; }
.title { font-size: 16px; font-weight: 700; margin: 6px 0 4px; }
.meta { font-size: 12px; overflow-wrap: anywhere; }
.ck-content { font-size: 12px; }
.sep { border-top: 1px dashed #000; margin: 6px 0; }
table { width: 100%; border-collapse: collapse; font-size: 12px; }
th, td { padding: 2px 1px; vertical-align: top; }
th { border-bottom: 1px solid #000; }
.l { text-align: left; }
.r { text-align: right; }
.c { text-align: center; }
.trow { display: flex; justify-content: space-between; font-size: 13px; margin-top: 2px; }
.trow.strong { font-weight: 700; font-size: 14px; margin-top: 4px; }
.qr { text-align: center; margin: 8px 0; }
.qr img { width: 30mm; height: 30mm; }
.footer { text-align: center; font-style: italic; margin-top: 8px; font-size: 12px; }
</style></head>
<body>
${logoBlock}<div class="center company">${escapeHtml(data.companyName)}</div>
${data.taxCode ? `<div class="center meta">${m.receipt_label_tax_code()}: ${escapeHtml(data.taxCode)}</div>` : ''}
${data.address ? `<div class="center meta">${escapeHtml(data.address)}</div>` : ''}
<div class="center title">${m.receipt_title()}</div>
<div class="center meta"><strong>${m.receipt_label_invoice_no()}: ${escapeHtml(data.orderCode)}</strong></div>
${dateLine ? `<div class="meta">${dateLine}</div>` : ''}
${metaLine(m.receipt_label_cashier(), data.cashierName ?? '')}
${metaLine(m.receipt_label_customer(), data.customerName ?? '')}
${metaLine(m.receipt_label_address(), data.customerAddress ?? '')}
${metaLine(m.receipt_label_phone(), data.customerPhone ?? '')}
${metaLine(m.receipt_label_payment(), paymentMethodLabel(data.paymentMethod))}
${ckLine}
<div class="sep"></div>
<table>
<thead><tr>
<th class="c">${m.receipt_col_no()}</th>
<th class="l">${m.receipt_col_product()}</th>
<th class="r">${m.receipt_col_unit_price()}</th>
<th class="c">${m.receipt_col_qty()}</th>
<th class="r">${m.receipt_col_amount()}</th>
</tr></thead>
<tbody>${rows}</tbody>
</table>
<div class="sep"></div>
${totalRow(m.receipt_subtotal(), data.totals.subtotal)}
${totalRow(m.receipt_discount(), data.totals.discount)}
${vatRow}
${totalRow(m.receipt_total(), data.totals.total, true)}
${qrBlock}
<div class="footer">${escapeHtml(footer)}</div>
</body></html>`
}
